import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Flame } from "lucide-react";
import { fileUrl, STORE_LABEL, STORE_COLOR, priceFmt, api, dealTitle } from "../lib/api";
import Countdown from "./Countdown";

export default function DealCard({ deal, idx = 0 }) {
  const title = dealTitle(deal);
  // Use a real <a> tag with target=_blank so the browser does NOT block it.
  // Tracking is fired in the background (no await on the open).
  const trackClick = () => {
    api.post(`/deals/${deal.id}/click`).catch(() => {});
  };

  return (
    <article
      data-testid={`deal-card-${deal.id}`}
      style={{ animationDelay: `${Math.min(idx, 12) * 40}ms` }}
      className="fade-up group relative bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden flex flex-col hover:-translate-y-1 hover:border-zinc-700 card-glow transition-all duration-300"
    >
      <Link to={`/offerta/${deal.id}`} data-testid={`deal-card-image-${deal.id}`} className="block">
        <div className="relative aspect-square w-full bg-zinc-950 overflow-hidden brick-pattern">
          {deal.image_url ? (
            <img
              src={fileUrl(deal.image_url)}
              alt={title}
              loading={idx < 4 ? "eager" : "lazy"}
              decoding="async"
              fetchPriority={idx < 4 ? "high" : "auto"}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-zinc-700 font-mono-bdd text-xs">
              NO IMG
            </div>
          )}

          <div
            className="absolute top-3 left-3 bg-zinc-950/85 backdrop-blur-md text-[10px] uppercase tracking-widest px-2 py-1 rounded-md border border-zinc-800 font-bold"
            style={{ color: STORE_COLOR[deal.store] || "#fff" }}
          >
            {STORE_LABEL[deal.store] || "Store"}
          </div>

          {deal.discount_percent ? (
            <div className="absolute top-3 right-3 bg-rose-600 text-white text-xs font-black px-2 py-1 rounded-md shadow-lg">
              -{deal.discount_percent}%
            </div>
          ) : null}

          {deal.hot ? (
            <div className="absolute bottom-3 left-3 inline-flex items-center gap-1 bg-[#FBD51C] text-black text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-md">
              <Flame size={11} /> Hot
            </div>
          ) : null}

          {typeof deal.deal_score === "number" && deal.deal_score >= 60 ? (
            <div
              data-testid={`deal-card-score-${deal.id}`}
              className={`absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] font-black px-2 py-1 rounded-md ${
                deal.deal_score >= 90
                  ? "bg-emerald-500 text-black"
                  : deal.deal_score >= 75
                    ? "bg-[#FBD51C] text-black"
                    : "bg-blue-500 text-white"
              }`}
              title={deal.deal_score_label}
            >
              {deal.deal_score}
            </div>
          ) : null}

          {deal.expires_at ? (
            <div className="absolute bottom-3 right-3">
              <Countdown to={deal.expires_at} compact />
            </div>
          ) : null}
        </div>
      </Link>

      <div className="p-4 flex flex-col flex-1 gap-3">
        <Link
          to={`/offerta/${deal.id}`}
          data-testid={`deal-card-title-${deal.id}`}
          className="font-display font-bold text-white text-[15px] leading-tight line-clamp-2 min-h-[36px] hover:text-[#FBD51C] transition-colors"
        >
          {title}
        </Link>

        <div className="flex items-end justify-between gap-2">
          <div>
            {deal.price_current ? (
              <div className="font-display font-black text-2xl text-white leading-none">
                {priceFmt(deal.price_current)}
              </div>
            ) : (
              <div className="font-mono-bdd text-xs text-zinc-500">
                Prezzo nel sito
              </div>
            )}
            {deal.price_original && deal.price_original > (deal.price_current || 0) ? (
              <div className="text-xs text-zinc-500 line-through mt-1">
                {priceFmt(deal.price_original)}
              </div>
            ) : null}
          </div>
          <a
            href={deal.affiliate_url}
            target="_blank"
            rel="noopener noreferrer sponsored"
            onClick={trackClick}
            data-testid={`deal-card-cta-${deal.id}`}
            className="shrink-0 inline-flex items-center gap-1.5 bg-[#FBD51C] hover:bg-[#F5C518] text-black text-xs font-black uppercase tracking-wider px-3 py-2 rounded-lg transition-colors"
          >
            Vai <ExternalLink size={12} />
          </a>
        </div>
      </div>
    </article>
  );
}
